import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Download, Filter, RefreshCw, Search, Upload, MapPin, Loader2, Trash2, Route as RouteIcon, X } from "lucide-react";
import { supabase } from "@/lib/supabase";
import {
  assignLeads,
  listLeads,
  listLeadSources,
  listLeadStates,
  softDeleteLeads,
  type LeadRow,
  type LeadStatus,
} from "@/services/leads";
import { listTeamMembers } from "@/services/team";
import { recordActivity } from "@/services/activityLog";
import { useIsAdmin, useIsFullAdmin } from "@/hooks/useCurrentUser";
import { useAuthStore } from "@/store/auth";
import { cn } from "@/lib/cn";
import { nextSelectionFromClick, pageSelectionState, togglePageSelection } from "@/lib/selection";
import { formatPhoneDisplay, toMailtoHref, toTelHref } from "@/lib/contactLinks";

const PAGE_SIZE_OPTIONS = [25, 50, 75, 100] as const;
type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];

// Labels are display-only. The underlying `LeadStatus` enum keeps `closed`
// (DB-safe). The UI label is "Sold" because in this product the only path
// to `lead.status = 'closed'` is the "Sold" outcome button on a stop, which
// means a closed-won sale in life-insurance terms.
const STATUS_FILTERS: { value: LeadStatus | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "new", label: "New" },
  { value: "no_answer", label: "No answer" },
  { value: "scheduled", label: "Scheduled" },
  { value: "follow_up", label: "Follow-up" },
  { value: "closed", label: "Sold" },
  { value: "do_not_solicit", label: "DNC" },
];

const STATUS_LABEL: Record<LeadStatus, string> = {
  new: "New",
  no_answer: "No answer",
  scheduled: "Scheduled",
  follow_up: "Follow-up",
  closed: "Sold",
  do_not_solicit: "DNC",
};

const STATUS_BADGE: Record<LeadStatus, string> = {
  new: "bg-sky-50 text-sky-700 ring-sky-200",
  no_answer: "bg-amber-50 text-amber-700 ring-amber-200",
  scheduled: "bg-violet-50 text-violet-700 ring-violet-200",
  follow_up: "bg-indigo-50 text-indigo-700 ring-indigo-200",
  closed: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  do_not_solicit: "bg-rose-50 text-rose-700 ring-rose-200",
};

type LeadListColumn =
  | "name"
  | "address"
  | "dob"
  | "phone"
  | "email"
  | "policy_count"
  | "household_annual_premium"
  | "status"
  | "geo"
  | "source"
  | "created";

const ALL_COLUMNS: Array<{ key: LeadListColumn; label: string }> = [
  { key: "name", label: "Name" },
  { key: "address", label: "Address" },
  { key: "dob", label: "Date of Birth" },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email" },
  { key: "policy_count", label: "Policy Count" },
  { key: "household_annual_premium", label: "Total Household Annual Premium" },
  { key: "status", label: "Status" },
  { key: "geo", label: "Geo" },
  { key: "source", label: "Source" },
  { key: "created", label: "Created" },
];

const DEFAULT_COLUMNS: LeadListColumn[] = [
  "name",
  "address",
  "dob",
  "phone",
  "email",
  "policy_count",
  "household_annual_premium",
];

const POLICY_COUNT_ALIASES = [
  "policycount",
  "policies",
  "totalpolicies",
  "policy_count",
  "policy count",
  "numberofpolicies",
];

const HOUSEHOLD_PREMIUM_ALIASES = [
  "totalhouseholdannualpremium",
  "householdannualpremium",
  "totalannualpremium",
  "combinedannualpremium",
  "total_household_annual_premium",
  "total household annual premium",
];

function normalizeKey(v: string): string {
  return v.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function getCustomValue(customFields: Record<string, unknown> | null | undefined, aliases: string[]): unknown {
  if (!customFields) return null;
  for (const alias of aliases) {
    if (alias in customFields) return customFields[alias];
  }
  const normalized = new Map<string, unknown>();
  for (const [k, v] of Object.entries(customFields)) {
    normalized.set(normalizeKey(k), v);
  }
  for (const alias of aliases) {
    const hit = normalized.get(normalizeKey(alias));
    if (hit != null) return hit;
  }
  return null;
}

function parseNumberLike(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const n = Number(String(v).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : null;
}

function formatCurrency(v: number | null): string {
  if (v == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(v);
}

function formatDob(isoDate: string | null | undefined): string {
  if (!isoDate) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!m) return isoDate;
  return `${m[2]}/${m[3]}/${m[1]}`;
}

function readStoredColumns(storageKey: string): LeadListColumn[] {
  if (typeof window === "undefined") return DEFAULT_COLUMNS;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return DEFAULT_COLUMNS;
    const parsed = JSON.parse(raw) as string[];
    const valid = parsed.filter((v): v is LeadListColumn => ALL_COLUMNS.some((c) => c.key === v));
    const ordered = ALL_COLUMNS.map((c) => c.key).filter((k) => valid.includes(k));
    return ordered.length > 0 ? ordered : DEFAULT_COLUMNS;
  } catch {
    return DEFAULT_COLUMNS;
  }
}

type GroupBy = "none" | "zip" | "state" | "city";
type SortBy = "created" | "location";

interface PersistedFilters {
  search: string;
  source: string;
  state: string;
  zip: string;
  city: string;
  ageMin: string;
  ageMax: string;
  premiumMin: string;
  premiumMax: string;
  groupBy: GroupBy;
  sortBy: SortBy;
  status: LeadStatus | "all";
  pageSize: PageSize;
}

const DEFAULT_FILTERS: PersistedFilters = {
  search: "",
  source: "",
  state: "",
  zip: "",
  city: "",
  ageMin: "",
  ageMax: "",
  premiumMin: "",
  premiumMax: "",
  groupBy: "none",
  sortBy: "created",
  status: "all",
  pageSize: 50,
};

/**
 * Builds an RFC-4180-ish CSV for the exported lead set. Escapes quotes by
 * doubling, wraps any cell containing comma / quote / newline in quotes.
 * We always include id + status_changed_at + status_changed_by metadata
 * regardless of which columns the user has visible — that's what makes the
 * export useful for sales-attribution reporting in spreadsheets.
 */
function csvEscape(v: unknown): string {
  if (v == null) return "";
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function buildLeadsCsv(rows: LeadRow[], visibleCols: LeadListColumn[]): string {
  // Header order: always-included audit fields first, then the user's
  // visible columns in the same order they appear in the table.
  const colDefs: Array<{ key: string; label: string; pick: (r: LeadRow) => unknown }> = [
    { key: "id", label: "id", pick: (r) => r.id },
    { key: "status", label: "status", pick: (r) => r.status },
    { key: "status_changed_at", label: "status_changed_at", pick: (r) => r.status_changed_at },
    { key: "status_changed_by", label: "status_changed_by", pick: (r) => r.status_changed_by ?? "" },
    { key: "assigned_to", label: "assigned_to", pick: (r) => r.assigned_to ?? "" },
  ];
  const optionalDefs: Record<LeadListColumn, { label: string; pick: (r: LeadRow) => unknown }> = {
    name: { label: "name", pick: (r) => r.full_name || [r.first_name, r.last_name].filter(Boolean).join(" ") },
    address: {
      label: "address",
      pick: (r) => r.address_combined || [r.address_street, r.address_city, r.address_state, r.address_zip].filter(Boolean).join(", "),
    },
    dob: { label: "dob", pick: (r) => r.dob ?? "" },
    phone: { label: "phone", pick: (r) => r.phone ?? "" },
    email: { label: "email", pick: (r) => r.email ?? "" },
    policy_count: { label: "policy_count", pick: (r) => getCustomValue(r.custom_fields, POLICY_COUNT_ALIASES) ?? "" },
    household_annual_premium: {
      label: "total_household_annual_premium",
      pick: (r) => getCustomValue(r.custom_fields, HOUSEHOLD_PREMIUM_ALIASES) ?? "",
    },
    status: { label: "status_label", pick: (r) => r.status },
    geo: { label: "lat,lng", pick: (r) => (r.lat != null && r.lng != null ? `${r.lat},${r.lng}` : "") },
    source: { label: "source", pick: (r) => r.source ?? "" },
    created: { label: "created_at", pick: (r) => r.created_at ?? "" },
  };
  for (const col of visibleCols) {
    if (col === "status") continue; // dedupe — already in always-included.
    colDefs.push({ key: col, label: optionalDefs[col].label, pick: optionalDefs[col].pick });
  }
  const header = colDefs.map((c) => csvEscape(c.label)).join(",");
  const lines = rows.map((r) => colDefs.map((c) => csvEscape(c.pick(r))).join(","));
  return [header, ...lines].join("\r\n");
}

function readStoredFilters(storageKey: string): PersistedFilters {
  if (typeof window === "undefined") return DEFAULT_FILTERS;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return DEFAULT_FILTERS;
    const parsed = JSON.parse(raw) as Partial<PersistedFilters>;
    return {
      search: typeof parsed.search === "string" ? parsed.search : "",
      source: typeof parsed.source === "string" ? parsed.source : "",
      state: typeof parsed.state === "string" ? parsed.state : "",
      zip: typeof parsed.zip === "string" ? parsed.zip : "",
      city: typeof parsed.city === "string" ? parsed.city : "",
      ageMin: typeof parsed.ageMin === "string" ? parsed.ageMin : "",
      ageMax: typeof parsed.ageMax === "string" ? parsed.ageMax : "",
      premiumMin: typeof parsed.premiumMin === "string" ? parsed.premiumMin : "",
      premiumMax: typeof parsed.premiumMax === "string" ? parsed.premiumMax : "",
      groupBy: (["none", "zip", "state", "city"] as const).includes(parsed.groupBy as never)
        ? (parsed.groupBy as GroupBy)
        : "none",
      sortBy: (["created", "location"] as const).includes(parsed.sortBy as never)
        ? (parsed.sortBy as SortBy)
        : "created",
      status: (["all", "new", "no_answer", "scheduled", "follow_up", "closed", "do_not_solicit"] as const).includes(parsed.status as never)
        ? (parsed.status as LeadStatus | "all")
        : "all",
      pageSize: PAGE_SIZE_OPTIONS.includes(parsed.pageSize as PageSize)
        ? (parsed.pageSize as PageSize)
        : 50,
    };
  } catch {
    return DEFAULT_FILTERS;
  }
}

export function LeadsList() {
  const isAdmin = useIsAdmin();
  // Lead delete + lead export are admin_user-only operations per the
  // permission spec (migration 0019). route_manager has admin-like access
  // to most surfaces but is explicitly blocked from these two. Server side
  // is enforced by the leads_delete RLS policy using is_full_admin(); the
  // export is a client-side action with no server gate, so this hook IS the
  // gate for export.
  const isFullAdmin = useIsFullAdmin();
  const user = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Drill-down URL params from the dashboard click-through. Read once on mount,
  // applied as one-shot overrides (so persisted filters resume next visit
  // without these specific values sticking). The dashboard pushes:
  //   ?status=<lead_status>&from=<ISO>&to=<ISO>&changedBy=<uuid>
  const urlStatus = searchParams.get("status");
  const urlFrom = searchParams.get("from");
  const urlTo = searchParams.get("to");
  const urlChangedBy = searchParams.get("changedBy");
  const hasUrlDrilldown = !!(urlStatus || urlFrom || urlTo || urlChangedBy);
  // Filter + column persistence is scoped per-team so admins switching tenants
  // don't carry filter state across orgs. Page index intentionally NOT
  // persisted — landing on page 7 after a fresh reload would be disorienting
  // when results may have changed.
  const filtersKey = useMemo(() => `myroute:leads:filters:v1:${user?.teamId ?? "anon"}`, [user?.teamId]);
  const initialFilters = useMemo(() => readStoredFilters(filtersKey), [filtersKey]);
  // When the URL carries a dashboard drill-down status, it wins over the
  // persisted filter — that's what the user clicked to see. Other persisted
  // filters (source/state/zip/search) stay intact so the admin doesn't lose
  // their working context.
  const [search, setSearch] = useState(initialFilters.search);
  const [source, setSource] = useState(initialFilters.source);
  const [state, setState] = useState(initialFilters.state);
  const [zip, setZip] = useState(initialFilters.zip);
  const [city, setCity] = useState(initialFilters.city);
  const [ageMin, setAgeMin] = useState(initialFilters.ageMin);
  const [ageMax, setAgeMax] = useState(initialFilters.ageMax);
  const [premiumMin, setPremiumMin] = useState(initialFilters.premiumMin);
  const [premiumMax, setPremiumMax] = useState(initialFilters.premiumMax);
  const [groupBy, setGroupBy] = useState<GroupBy>(initialFilters.groupBy);
  const [sortBy, setSortBy] = useState<SortBy>(initialFilters.sortBy);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [status, setStatus] = useState<LeadStatus | "all">(() => {
    if (urlStatus && (["new", "no_answer", "scheduled", "follow_up", "closed", "do_not_solicit"] as const).includes(urlStatus as never)) {
      return urlStatus as LeadStatus;
    }
    return initialFilters.status;
  });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<PageSize>(initialFilters.pageSize);
  // Date-range + changed-by filters used by the dashboard drill-down. Not
  // persisted (intentional — they're transient drill-down context, not the
  // user's working filter set).
  const [changedFrom, setChangedFrom] = useState<string | null>(urlFrom);
  const [changedTo, setChangedTo] = useState<string | null>(urlTo);
  const [changedBy, setChangedBy] = useState<string | null>(urlChangedBy);
  const storageKey = useMemo(() => `myroute:leads:listColumns:v1:${user?.teamId ?? "anon"}`, [user?.teamId]);
  const [visibleColumns, setVisibleColumns] = useState<LeadListColumn[]>(() => readStoredColumns(storageKey));
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const lastClickedIdRef = useRef<string | null>(null);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const columnsMenuRef = useRef<HTMLDivElement | null>(null);
  const tableScrollerRef = useRef<HTMLDivElement | null>(null);
  const [showLeftScrollCue, setShowLeftScrollCue] = useState(false);
  const [showRightScrollCue, setShowRightScrollCue] = useState(false);

  // Distinct values for the source + state dropdowns — long staleTime, RLS-scoped.
  const sourcesQuery = useQuery({
    queryKey: ["leads", "distinct-sources"],
    queryFn: listLeadSources,
    staleTime: 5 * 60_000,
  });
  // Active members for the bulk "Assign to…" control — elevated roles only.
  const membersQuery = useQuery({
    queryKey: ["team", "members"],
    queryFn: listTeamMembers,
    enabled: isAdmin,
    staleTime: 5 * 60_000,
  });
  const statesQuery = useQuery({
    queryKey: ["leads", "distinct-states"],
    queryFn: listLeadStates,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    setVisibleColumns(readStoredColumns(storageKey));
  }, [storageKey]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(storageKey, JSON.stringify(visibleColumns));
  }, [storageKey, visibleColumns]);

  // Persist filters back on every change so a reload (or new tab on same
  // team) lands the user where they left off.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const payload: PersistedFilters = { search, source, state, zip, city, ageMin, ageMax, premiumMin, premiumMax, groupBy, sortBy, status, pageSize };
    window.localStorage.setItem(filtersKey, JSON.stringify(payload));
  }, [filtersKey, search, source, state, zip, ageMin, ageMax, groupBy, status, pageSize]);

  useEffect(() => {
    if (!columnsOpen) return;

    function onPointerDown(event: MouseEvent) {
      if (!columnsMenuRef.current) return;
      if (!columnsMenuRef.current.contains(event.target as Node)) {
        setColumnsOpen(false);
      }
    }

    function onEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setColumnsOpen(false);
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onEscape);
    };
  }, [columnsOpen]);

  // Filter changes invalidate selection (different scope of rows); paging
  // does NOT — that lets users tick rows across pages into a single route.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [search, source, state, zip, city, ageMin, ageMax, premiumMin, premiumMax, status]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["leads", "list", { search, source, state, zip, city, ageMin, ageMax, premiumMin, premiumMax, sortBy, status, changedFrom, changedTo, changedBy, page, pageSize }],
    queryFn: () => listLeads({
      search, source, state, zip, city,
      ageMin: ageMin !== "" ? parseInt(ageMin, 10) : null,
      ageMax: ageMax !== "" ? parseInt(ageMax, 10) : null,
      premiumMin: premiumMin !== "" ? parseFloat(premiumMin) : null,
      premiumMax: premiumMax !== "" ? parseFloat(premiumMax) : null,
      sortBy,
      status, changedFrom, changedTo, changedBy, page, pageSize,
    }),
    placeholderData: (prev) => prev,
  });

  // Clear URL drill-down params after they've been applied to state. Without
  // this, hitting browser-back later would re-apply them as new initial state.
  useEffect(() => {
    if (hasUrlDrilldown) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function clearDrilldown() {
    setChangedFrom(null);
    setChangedTo(null);
    setChangedBy(null);
    setStatus("all");
    setPage(0);
  }

  const total = data?.total ?? 0;
  const rows = data?.rows ?? [];
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageIds = rows.map((r) => r.id);
  const pageSelState = pageSelectionState(pageIds, selectedIds);

  // Universal clamp: if the current page falls off the end of the list (bulk
  // delete, another admin's delete arriving via realtime, a filter change
  // shrinking the set), snap to the new last page instead of showing an
  // empty page that only a reload used to fix.
  useEffect(() => {
    if (page > totalPages - 1) setPage(Math.max(0, totalPages - 1));
  }, [page, totalPages]);

  // Sort rows for group-by display (client-side on current page only).
  const displayRows = useMemo(() => {
    if (groupBy === "none") return rows;
    return [...rows].sort((a, b) => {
      if (groupBy === "zip") {
        return (a.address_zip ?? "").localeCompare(b.address_zip ?? "");
      }
      if (groupBy === "state") {
        return (a.address_state ?? "").localeCompare(b.address_state ?? "");
      }
      if (groupBy === "city") {
        const sc = (a.address_state ?? "").localeCompare(b.address_state ?? "");
        if (sc !== 0) return sc;
        return (a.address_city ?? "").localeCompare(b.address_city ?? "");
      }
      return 0;
    });
  }, [rows, groupBy]);

  // Count ungeooded leads in the current page for the geocode button badge.
  const ungeocodedInPage = rows.filter((r) => r.lat == null).length;

  useEffect(() => {
    if (!rows.length) {
      setShowLeftScrollCue(false);
      setShowRightScrollCue(false);
      return;
    }

    if (!tableScrollerRef.current) return;
    const scroller = tableScrollerRef.current;

    function syncScrollCues() {
      const maxLeft = scroller.scrollWidth - scroller.clientWidth;
      const canScroll = maxLeft > 1;
      setShowLeftScrollCue(canScroll && scroller.scrollLeft > 1);
      setShowRightScrollCue(canScroll && scroller.scrollLeft < maxLeft - 1);
    }

    syncScrollCues();
    scroller.addEventListener("scroll", syncScrollCues);
    window.addEventListener("resize", syncScrollCues);
    return () => {
      scroller.removeEventListener("scroll", syncScrollCues);
      window.removeEventListener("resize", syncScrollCues);
    };
  }, [rows.length, visibleColumns, isAdmin]);

  const deleteMut = useMutation({
    mutationFn: async (ids: string[]) => {
      if (!user) throw new Error("Not signed in");
      return softDeleteLeads(ids, user.id);
    },
    // Optimistic removal: strip the deleted rows out of EVERY cached
    // ["leads","list",...] page and shrink totals immediately. Without this,
    // placeholderData kept the stale page (deleted rows included) visible
    // until the background refetch landed — which read as "delete didn't
    // work" — and emptying the last page left an out-of-range page index.
    onMutate: async (ids: string[]) => {
      await qc.cancelQueries({ queryKey: ["leads"] });
      const idSet = new Set(ids);
      const snapshots = qc.getQueriesData<{ rows: LeadRow[]; total: number }>({ queryKey: ["leads", "list"] });
      for (const [key, cached] of snapshots) {
        if (!cached) continue;
        const kept = cached.rows.filter((r) => !idSet.has(r.id));
        qc.setQueryData(key, { rows: kept, total: Math.max(0, cached.total - ids.length) });
      }
      return { snapshots };
    },
    onError: (e: Error, _ids, context) => {
      // Roll the optimistic removal back — e.g. RLS denied the delete.
      for (const [key, cached] of context?.snapshots ?? []) {
        qc.setQueryData(key, cached);
      }
      toast.error(e.message);
    },
    onSuccess: (count, ids) => {
      toast.success(`Deleted ${count} lead${count === 1 ? "" : "s"}`);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of ids) next.delete(id);
        return next;
      });
      // Page clamping is handled by the totalPages effect above, which sees
      // the post-optimistic totals.
    },
    // Reconcile with the server regardless of outcome.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["leads"] });
    },
  });

  const assignMut = useMutation({
    mutationFn: ({ ids, assignee }: { ids: string[]; assignee: string | null }) => assignLeads(ids, assignee),
    onSuccess: (count, { assignee }) => {
      const member = (membersQuery.data ?? []).find((m) => m.id === assignee);
      toast.success(
        assignee
          ? `Assigned ${count} lead${count === 1 ? "" : "s"} to ${member?.fullName ?? "member"}`
          : `Unassigned ${count} lead${count === 1 ? "" : "s"}`,
      );
      setSelectedIds(new Set());
      void qc.invalidateQueries({ queryKey: ["leads"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const geocodeMut = useMutation({
    mutationFn: () =>
      supabase.functions.invoke<{ processed: number; geocoded: number; failed: number }>("geocode-leads", {
        body: { limit: 500 },
      }),
    onSuccess: (r) => {
      const d = r.data;
      toast.success(
        d ? `Geocoded ${d.geocoded} of ${d.processed} leads (${d.failed} failed)` : "Geocoding triggered",
      );
      void qc.invalidateQueries({ queryKey: ["leads"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function toggleColumn(col: LeadListColumn) {
    setVisibleColumns((prev) => {
      if (prev.includes(col)) {
        const next = prev.filter((c) => c !== col);
        return next.length ? next : prev;
      }
      const next = [...prev, col];
      return ALL_COLUMNS.map((c) => c.key).filter((k) => next.includes(k));
    });
  }

  function toggleOne(id: string, shiftKey: boolean) {
    setSelectedIds((prev) => nextSelectionFromClick(id, shiftKey, pageIds, lastClickedIdRef.current, prev));
    lastClickedIdRef.current = id;
  }

  function togglePage() {
    setSelectedIds((prev) => togglePageSelection(pageIds, prev));
    lastClickedIdRef.current = null;
  }

  function confirmDelete(ids: string[]) {
    if (!ids.length || deleteMut.isPending) return;
    const label = ids.length === 1 ? "this lead" : `${ids.length} leads`;
    if (!confirm(`Delete ${label}? This performs a soft-delete.`)) return;
    deleteMut.mutate(ids);
  }

  function planRouteFromSelection() {
    if (selectedIds.size === 0) return;
    const ids = [...selectedIds].join(",");
    navigate(`/routes/plan?leadIds=${encodeURIComponent(ids)}`);
  }

  function clearAllFilters() {
    setSearch(""); setSource(""); setState(""); setZip(""); setCity(""); setAgeMin(""); setAgeMax(""); setPremiumMin(""); setPremiumMax(""); setStatus("all");
    setChangedFrom(null); setChangedTo(null); setChangedBy(null);
    setPage(0);
  }

  // ---- CSV export (admin-only) ---------------------------------------------
  // Streams matching leads in 500-row pages until exhausted so we don't hit
  // Supabase's max_rows cap with .range. Respects every active filter.
  // Logs an activity_log entry so admins can audit who pulled data.
  const exportMut = useMutation({
    mutationFn: async () => {
      const PAGE = 500;
      const all: LeadRow[] = [];
      let p = 0;
      while (true) {
        const batch = await listLeads({
          search, source, state, zip, city,
          ageMin: ageMin !== "" ? parseInt(ageMin, 10) : null,
          ageMax: ageMax !== "" ? parseInt(ageMax, 10) : null,
          premiumMin: premiumMin !== "" ? parseFloat(premiumMin) : null,
          premiumMax: premiumMax !== "" ? parseFloat(premiumMax) : null,
          sortBy,
          status, changedFrom, changedTo, changedBy,
          page: p, pageSize: PAGE,
        });
        all.push(...batch.rows);
        if (batch.rows.length < PAGE) break;
        p++;
        if (all.length > 50_000) {
          throw new Error("Export refused — 50,000 row limit hit. Tighten filters first.");
        }
      }
      const csv = buildLeadsCsv(all, visibleColumns);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const filename = `leads-${ts}.csv`;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      void recordActivity({
        kind: "lead_export",
        summary: `Exported ${all.length} leads (${visibleColumns.length} cols)`,
        metadata: {
          row_count: all.length,
          columns: visibleColumns,
          filters: { search, source, state, zip, status, changedFrom, changedTo, changedBy },
          filename,
        },
      });
      return { count: all.length, filename };
    },
    onSuccess: (r) => toast.success(`Exported ${r.count} leads as ${r.filename}`),
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-4">
      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
          <span className="text-slate-700">
            <span className="font-semibold">{selectedIds.size}</span> selected
          </span>
          <div className="flex flex-wrap items-center gap-2">
            {isAdmin && (
              <select
                value=""
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) return;
                  assignMut.mutate({ ids: [...selectedIds], assignee: v === "__unassign" ? null : v });
                  e.currentTarget.value = "";
                }}
                disabled={assignMut.isPending}
                className="rounded-md border-slate-300 py-1.5 pl-2 pr-7 text-xs font-semibold text-slate-700 shadow-sm focus:border-brand-500 focus:ring-brand-500 disabled:opacity-50"
              >
                <option value="">{assignMut.isPending ? "Assigning…" : "Assign to…"}</option>
                {(membersQuery.data ?? []).filter((m) => m.isActive).map((m) => (
                  <option key={m.id} value={m.id}>{m.fullName}</option>
                ))}
                <option value="__unassign">— Unassign —</option>
              </select>
            )}
            <button
              type="button"
              onClick={planRouteFromSelection}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-brand-700"
            >
              <RouteIcon className="h-3.5 w-3.5" />
              Plan route from selected
            </button>
            {isFullAdmin && (
              <button
                type="button"
                onClick={() => confirmDelete([...selectedIds])}
                disabled={deleteMut.isPending}
                className="inline-flex items-center gap-1.5 rounded-md border border-rose-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
                {deleteMut.isPending ? "Deleting…" : "Delete selected"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div className="space-y-2">
        {/* Row 1: search + mobile filter toggle + action buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-0 flex-1 sm:min-w-[16rem]">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              placeholder="Search name, email, phone…"
              className="w-full rounded-md border-slate-300 pl-8 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
            />
          </div>

          {/* Mobile: toggle extended filters */}
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm font-medium shadow-sm sm:hidden",
              (zip || state || source || city || ageMin || ageMax || premiumMin || premiumMax || status !== "all")
                ? "border-brand-400 bg-brand-50 text-brand-700"
                : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50",
            )}
          >
            <Filter className="h-4 w-4" />
            Filters
          </button>

          {/* Action buttons — always visible */}
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <div ref={columnsMenuRef} className="relative">
              <button
                type="button"
                onClick={() => setColumnsOpen((v) => !v)}
                aria-expanded={columnsOpen}
                aria-haspopup="menu"
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Columns
              </button>
              {columnsOpen && (
                <div className="absolute right-0 top-full z-30 mt-2 max-h-72 w-64 overflow-y-auto rounded-md border border-slate-200 bg-white p-2 shadow-lg">
                  <div className="grid gap-1">
                    {ALL_COLUMNS.map((col) => (
                      <label key={col.key} className="inline-flex items-center gap-2 text-xs text-slate-700">
                        <input
                          type="checkbox"
                          checked={visibleColumns.includes(col.key)}
                          onChange={() => toggleColumn(col.key)}
                          className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                        />
                        {col.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {isAdmin && (
              <button
                type="button"
                onClick={() => geocodeMut.mutate()}
                disabled={geocodeMut.isPending}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                title={`Run geocoding on un-located leads${ungeocodedInPage > 0 ? ` (${ungeocodedInPage} on this page missing geo)` : ""}`}
              >
                <RefreshCw className={cn("h-4 w-4", geocodeMut.isPending && "animate-spin")} />
                <span className="hidden sm:inline">{geocodeMut.isPending ? "Geocoding…" : "Geocode"}</span>
              </button>
            )}

            {isFullAdmin && (
              <button
                type="button"
                onClick={() => exportMut.mutate()}
                disabled={exportMut.isPending || total === 0}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                title="Export every lead matching the current filters as CSV"
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">{exportMut.isPending ? "Exporting…" : "Export"}</span>
              </button>
            )}
            {isAdmin && (
              <Link
                to="/leads/import"
                className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
              >
                <Upload className="h-4 w-4" />
                <span className="hidden sm:inline">Import CSV</span>
              </Link>
            )}
          </div>
        </div>

        {/* Row 2: extended filters — always on desktop, toggle on mobile */}
        <div className={cn("space-y-2", filtersOpen ? "block" : "hidden sm:block")}>
          <div className="flex flex-wrap items-center gap-2">
            {/* City */}
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={city}
                onChange={(e) => { setCity(e.target.value); setPage(0); }}
                placeholder="City…"
                className="w-32 rounded-md border-slate-300 pl-7 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:w-36"
              />
            </div>

            {/* Multi-ZIP */}
            <input
              type="text"
              value={zip}
              onChange={(e) => { setZip(e.target.value); setPage(0); }}
              placeholder="ZIP(s): 12345 or 12345,67890"
              className="w-44 rounded-md border-slate-300 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:w-48"
            />

            {/* State */}
            <select
              value={state}
              onChange={(e) => { setState(e.target.value); setPage(0); }}
              className="w-28 rounded-md border-slate-300 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
            >
              <option value="">All states</option>
              {(statesQuery.data ?? []).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>

            {/* Source */}
            <select
              value={source}
              onChange={(e) => { setSource(e.target.value); setPage(0); }}
              className="w-36 rounded-md border-slate-300 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
            >
              <option value="">All sources</option>
              {(sourcesQuery.data ?? []).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>

            {/* Age range */}
            <div className="flex items-center gap-1">
              <span className="text-xs font-medium text-slate-500">Age</span>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                max={120}
                value={ageMin}
                onChange={(e) => { setAgeMin(e.target.value); setPage(0); }}
                placeholder="min"
                className="w-16 rounded-md border-slate-300 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
              />
              <span className="text-xs text-slate-400">–</span>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                max={120}
                value={ageMax}
                onChange={(e) => { setAgeMax(e.target.value); setPage(0); }}
                placeholder="max"
                className="w-16 rounded-md border-slate-300 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
              />
            </div>

            {/* Annual premium range */}
            <div className="flex items-center gap-1">
              <span className="text-xs font-medium text-slate-500">Premium $</span>
              <input
                type="number"
                inputMode="decimal"
                min={0}
                value={premiumMin}
                onChange={(e) => { setPremiumMin(e.target.value); setPage(0); }}
                placeholder="min"
                className="w-20 rounded-md border-slate-300 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
              />
              <span className="text-xs text-slate-400">–</span>
              <input
                type="number"
                inputMode="decimal"
                min={0}
                value={premiumMax}
                onChange={(e) => { setPremiumMax(e.target.value); setPage(0); }}
                placeholder="max"
                className="w-20 rounded-md border-slate-300 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
              />
            </div>

            {/* Group by */}
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as GroupBy)}
              className="w-36 rounded-md border-slate-300 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
            >
              <option value="none">No grouping</option>
              <option value="city">Group by City</option>
              <option value="zip">Group by ZIP</option>
              <option value="state">Group by State</option>
            </select>

            {/* Sort by */}
            <select
              value={sortBy}
              onChange={(e) => { setSortBy(e.target.value as SortBy); setPage(0); }}
              className="w-36 rounded-md border-slate-300 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
            >
              <option value="created">Newest first</option>
              <option value="location">Sort by location</option>
            </select>
          </div>

          {/* Status pills */}
          <div className="flex flex-wrap items-center gap-1.5">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => { setStatus(f.value); setPage(0); }}
                className={cn(
                  "rounded-full px-3 py-1 text-xs font-medium ring-1 transition",
                  status === f.value
                    ? "bg-brand-600 text-white ring-brand-600"
                    : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50",
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {(changedFrom || changedTo || changedBy) && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
          <span>
            Dashboard drill-down active —
            {changedFrom && ` from ${new Date(changedFrom).toLocaleDateString()}`}
            {changedTo && ` to ${new Date(changedTo).toLocaleDateString()}`}
            {changedBy && " · this user only"}
            {status !== "all" && ` · status: ${STATUS_LABEL[status as LeadStatus] ?? status}`}
          </span>
          <button
            type="button"
            onClick={clearDrilldown}
            className="inline-flex items-center gap-1 rounded-md border border-sky-300 bg-white px-2 py-0.5 font-semibold text-sky-700 hover:bg-sky-100"
          >
            <X className="h-3 w-3" />
            Clear
          </button>
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center rounded-lg border border-slate-200 bg-white py-16 text-sm text-slate-500">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading leads…
        </div>
      )}

      {isError && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {(error as Error).message}
        </div>
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <EmptyState
          isAdmin={isAdmin}
          hasFilters={search.length > 0 || source.length > 0 || state.length > 0 || zip.length > 0 || status !== "all"}
          onClearFilters={clearAllFilters}
        />
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <div className="relative max-w-full rounded-lg border border-slate-200 bg-white">
          <div
            ref={tableScrollerRef}
            className="max-h-[65vh] w-full max-w-full overflow-x-auto overflow-y-auto"
          >
            <table className="w-max min-w-full divide-y divide-slate-200">
              <thead className="sticky top-0 z-10 bg-slate-50">
              <tr>
                {isAdmin && (
                  <Th className="w-10">
                    <input
                      type="checkbox"
                      checked={pageSelState === "all"}
                      ref={(el) => {
                        if (el) el.indeterminate = pageSelState === "some";
                      }}
                      onChange={togglePage}
                      aria-label={pageSelState === "none" ? "Select all leads on this page" : "Clear selection on this page"}
                      className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                    />
                  </Th>
                )}
                {visibleColumns.includes("name") && <Th className="min-w-[13rem]">Name</Th>}
                {visibleColumns.includes("address") && <Th className="min-w-[15rem]">Address</Th>}
                {visibleColumns.includes("dob") && <Th className="min-w-[8.5rem]">Date of Birth</Th>}
                {visibleColumns.includes("phone") && <Th className="min-w-[10rem]">Phone</Th>}
                {visibleColumns.includes("email") && <Th className="min-w-[14rem]">Email</Th>}
                {visibleColumns.includes("policy_count") && <Th className="min-w-[8rem] text-right">Policy Count</Th>}
                {visibleColumns.includes("household_annual_premium") && <Th className="min-w-[12rem] text-right">Total Household Annual Premium</Th>}
                {visibleColumns.includes("status") && <Th className="min-w-[8rem]">Status</Th>}
                {visibleColumns.includes("geo") && <Th className="min-w-[5rem] text-right">Geo</Th>}
                {visibleColumns.includes("source") && <Th className="min-w-[10rem]">Source</Th>}
                {visibleColumns.includes("created") && <Th className="min-w-[8rem]">Created</Th>}
                {isFullAdmin && <Th className="min-w-[8rem] text-right">Actions</Th>}
              </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
              {displayRows.map((lead, idx) => {
                const prevLead = idx > 0 ? displayRows[idx - 1] : null;
                const groupKey =
                  groupBy === "zip" ? lead.address_zip :
                  groupBy === "state" ? lead.address_state :
                  groupBy === "city" ? `${lead.address_state ?? ""} · ${lead.address_city ?? ""}` : null;
                const prevKey =
                  prevLead && (
                    groupBy === "zip" ? prevLead.address_zip :
                    groupBy === "state" ? prevLead.address_state :
                    groupBy === "city" ? `${prevLead.address_state ?? ""} · ${prevLead.address_city ?? ""}` : null
                  );
                const showGroupHeader = groupBy !== "none" && groupKey !== prevKey;
                const colSpan =
                  visibleColumns.length +
                  (isAdmin ? 1 : 0) +
                  (isFullAdmin ? 1 : 0);

                const name = lead.full_name
                  || [lead.first_name, lead.last_name].filter(Boolean).join(" ")
                  || "—";
                const address = lead.address_combined
                  || [lead.address_street, lead.address_city, lead.address_state, lead.address_zip]
                      .filter(Boolean).join(", ")
                  || "—";
                const policyCount = parseNumberLike(getCustomValue(lead.custom_fields, POLICY_COUNT_ALIASES));
                const householdAnnualPremium = parseNumberLike(
                  getCustomValue(lead.custom_fields, HOUSEHOLD_PREMIUM_ALIASES),
                );
                const isSelected = selectedIds.has(lead.id);
                return (
                  <Fragment key={lead.id}>
                    {showGroupHeader && (
                      <tr className="bg-slate-100">
                        <td
                          colSpan={colSpan}
                          className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"
                        >
                          {groupBy === "zip" ? `ZIP ${groupKey ?? "—"}` :
                           groupBy === "city" ? (lead.address_city ?? "—") + (lead.address_state ? `, ${lead.address_state}` : "") :
                           `State: ${groupKey ?? "—"}`}
                        </td>
                      </tr>
                    )}
                    <tr className={`hover:bg-slate-50 ${isSelected ? "bg-sky-50/70" : ""}`}>
                    {isAdmin && (
                      <td className="whitespace-nowrap px-4 py-2.5">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(lead.id)}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleOne(lead.id, e.shiftKey);
                          }}
                          onChange={() => { /* handled in onClick to capture shiftKey */ }}
                          aria-label={`Select lead ${name}`}
                          className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                        />
                      </td>
                    )}
                    {visibleColumns.includes("name") && (
                      <td className="min-w-[13rem] whitespace-nowrap px-4 py-2.5">
                        <div className="text-sm font-medium text-slate-900">{name}</div>
                        {lead.source && <div className="text-xs text-slate-500">{lead.source}</div>}
                      </td>
                    )}
                    {visibleColumns.includes("address") && (
                      <td className="min-w-[15rem] px-4 py-2.5 text-sm text-slate-600">{address}</td>
                    )}
                    {visibleColumns.includes("dob") && (
                      <td className="min-w-[8.5rem] whitespace-nowrap px-4 py-2.5 text-sm text-slate-600">
                        {formatDob(lead.dob)}
                      </td>
                    )}
                    {visibleColumns.includes("phone") && (
                      <td className="min-w-[10rem] whitespace-nowrap px-4 py-2.5 text-sm text-slate-600">
                        <PhoneLink raw={lead.phone} />
                      </td>
                    )}
                    {visibleColumns.includes("email") && (
                      <td className="min-w-[14rem] whitespace-nowrap px-4 py-2.5 text-sm text-slate-600">
                        <EmailLink raw={lead.email} />
                      </td>
                    )}
                    {visibleColumns.includes("policy_count") && (
                      <td className="min-w-[8rem] whitespace-nowrap px-4 py-2.5 text-right text-sm text-slate-600">
                        {policyCount != null ? Math.round(policyCount).toLocaleString() : "—"}
                      </td>
                    )}
                    {visibleColumns.includes("household_annual_premium") && (
                      <td className="min-w-[12rem] whitespace-nowrap px-4 py-2.5 text-right text-sm text-slate-600">
                        {formatCurrency(householdAnnualPremium)}
                      </td>
                    )}
                    {visibleColumns.includes("status") && (
                      <td className="min-w-[8rem] whitespace-nowrap px-4 py-2.5">
                        <span className={cn(
                          "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
                          STATUS_BADGE[lead.status],
                        )}>
                          {STATUS_LABEL[lead.status]}
                        </span>
                      </td>
                    )}
                    {visibleColumns.includes("geo") && (
                      <td className="min-w-[5rem] whitespace-nowrap px-4 py-2.5 text-right">
                        {lead.lat != null ? (
                          <MapPin className="ml-auto h-4 w-4 text-emerald-500" />
                        ) : (
                          <MapPin className="ml-auto h-4 w-4 text-slate-300" />
                        )}
                      </td>
                    )}
                    {visibleColumns.includes("source") && (
                      <td className="min-w-[10rem] whitespace-nowrap px-4 py-2.5 text-sm text-slate-600">{lead.source || "—"}</td>
                    )}
                    {visibleColumns.includes("created") && (
                      <td className="min-w-[8rem] whitespace-nowrap px-4 py-2.5 text-sm text-slate-600">
                        {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : "—"}
                      </td>
                    )}
                    {isFullAdmin && (
                      <td className="min-w-[8rem] whitespace-nowrap px-4 py-2.5 text-right">
                        <button
                          type="button"
                          onClick={() => confirmDelete([lead.id])}
                          disabled={deleteMut.isPending}
                          className="inline-flex items-center gap-1 rounded-md border border-rose-300 bg-white px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Delete
                        </button>
                      </td>
                    )}
                    </tr>
                  </Fragment>
                );
              })}
              </tbody>
            </table>
          </div>
          <div
            className={cn(
              "pointer-events-none absolute bottom-0 left-0 top-0 w-6 bg-gradient-to-r from-white to-transparent transition-opacity",
              showLeftScrollCue ? "opacity-100" : "opacity-0",
            )}
            aria-hidden="true"
          />
          <div
            className={cn(
              "pointer-events-none absolute bottom-0 right-0 top-0 w-6 bg-gradient-to-l from-white to-transparent transition-opacity",
              showRightScrollCue ? "opacity-100" : "opacity-0",
            )}
            aria-hidden="true"
          />
        </div>
      )}

      {!isLoading && !isError && total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
          <span>
            {(page * pageSize + 1).toLocaleString()}–
            {Math.min((page + 1) * pageSize, total).toLocaleString()} of {total.toLocaleString()}
          </span>
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-2 text-xs text-slate-500">
              Per page
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value) as PageSize); setPage(0); }}
                className="rounded-md border-slate-300 py-1 pl-2 pr-7 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="rounded-md border border-slate-300 bg-white px-3 py-1 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="rounded-md border border-slate-300 bg-white px-3 py-1 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={cn("px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500", className)}
    >
      {children}
    </th>
  );
}

function EmptyState({
  isAdmin,
  hasFilters,
  onClearFilters,
}: {
  isAdmin: boolean;
  hasFilters: boolean;
  onClearFilters: () => void;
}) {
  if (hasFilters) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center">
        <p className="text-sm font-medium text-slate-700">No leads match those filters.</p>
        <p className="mt-1 text-xs text-slate-500">Clear them to see everything.</p>
        <button
          type="button"
          onClick={onClearFilters}
          className="mt-4 inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
        >
          Clear filters
        </button>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center">
      <p className="text-sm font-medium text-slate-700">No leads yet.</p>
      <p className="mt-1 text-xs text-slate-500">
        {isAdmin
          ? "Upload a CSV to get started — column mapping and dedupe happen on the next screen."
          : "Ask your team admin to import leads, then they'll show up here."}
      </p>
      {isAdmin && (
        <Link
          to="/leads/import"
          className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
        >
          <Upload className="h-4 w-4" />
          Import CSV
        </Link>
      )}
    </div>
  );
}

/**
 * Clickable phone link with US-friendly formatting. Falls back to plain text
 * when the input doesn't normalise (international malformed numbers,
 * extensions). `onClick` stops propagation so clicking the link doesn't also
 * trigger row-selection logic that lives on the surrounding <tr>.
 */
function PhoneLink({ raw }: { raw: string | null | undefined }) {
  if (!raw) return <span>—</span>;
  const href = toTelHref(raw);
  const display = formatPhoneDisplay(raw) || raw;
  if (!href) return <span>{display}</span>;
  return (
    <a
      href={href}
      onClick={(e) => e.stopPropagation()}
      className="text-sky-700 hover:underline"
    >
      {display}
    </a>
  );
}

function EmailLink({ raw }: { raw: string | null | undefined }) {
  if (!raw) return <span>—</span>;
  const href = toMailtoHref(raw);
  if (!href) return <span>{raw}</span>;
  return (
    <a
      href={href}
      onClick={(e) => e.stopPropagation()}
      className="text-sky-700 hover:underline"
    >
      {raw}
    </a>
  );
}
